import React, { useState } from 'react';
import { Table, Button, Badge, ButtonGroup } from 'react-bootstrap';
import ConfirmDialog from './ConfirmDialog';
import { Contact } from '../../models/contact';

interface ContactTableProps {
  contacts: Contact[];
  onUpdateContact: (contact: Contact) => void;
  onDeleteContact: (contactId: number) => void;
}

const ContactTable: React.FC<ContactTableProps> = ({
  contacts,
  onUpdateContact,
  onDeleteContact,
}) => {
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [dialogType, setDialogType] = useState<'edit' | 'delete'>('edit');
  const [showDialog, setShowDialog] = useState<boolean>(false);

  // 編集ボタンの処理
  const handleEditClick = (contact: Contact): void => {
    setSelectedContact(contact);
    setDialogType('edit');
    setShowDialog(true);
  };

  // 削除ボタンの処理
  const handleDeleteClick = (contact: Contact): void => {
    setSelectedContact(contact);
    setDialogType('delete');
    setShowDialog(true);
  };

  // ダイアログの結果を受け取る
  const handleDialogConfirm = (updatedContact: Contact): void => {
    if (dialogType === 'edit') {
      onUpdateContact(updatedContact);
    } else if (dialogType === 'delete' && selectedContact) {
      onDeleteContact(selectedContact.id);
    }
    setShowDialog(false);
  };

  return (
    <>
      <div className="table-responsive">
        <Table className="user-table">
          <thead>
            <tr>
              <th style={{ width: '80px' }}>
                <i className="fas fa-id-card"></i>ID
              </th>
              <th style={{ width: '280px' }}>
                <i className="fas fa-user"></i>名前
              </th>
              <th style={{ width: '300px' }}>
                <i className="fas fa-envelope"></i>メールアドレス
              </th>
              <th style={{ width: '140px' }}>
                <i className="fas fa-toggle-on"></i>お問い合わせ内容
              </th>
              <th style={{ width: '180px' }}>
                <i className="fas fa-cogs"></i>操作
              </th>
            </tr>
          </thead>
          <tbody>
            {contacts.length > 0 ? (
              contacts.map((contact, index) => (
                <tr
                  key={contact.id}
                  className={index % 2 === 0 ? 'table-row-even' : ''}
                >
                  <td className="text-center">
                    <Badge className="user-id-badge">{contact.id}</Badge>
                  </td>
                  <td>
                    <div className="user-info">
                      <div className="user-avatar">
                        <i className="fas fa-user-circle"></i>
                      </div>
                      <div className="user-details">
                        <div className="contactname">{contact.name}</div>
                        <small>お問い合わせ者氏名</small>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className="email-info">
                      <i className="fas fa-at"></i>
                      <span className="email-text">{contact.email}</span>
                    </div>
                  </td>
                  <td className="text-center">
                    <div className="email-info">
                      <i className="fas fa-at"></i>
                      <span className="email-text">{contact.message}</span>
                    </div>
                  </td>
                  <td className="text-center">
                    <ButtonGroup size="sm" className="action-buttons">
                      <Button
                        variant="outline-primary"
                        onClick={() => handleEditClick(contact)}
                        className="action-btn edit-btn"
                        title="お問い合わせ情報を編集"
                      >
                        <i className="fas fa-edit"></i>
                        <span className="btn-text">編集</span>
                      </Button>
                      <Button
                        variant="outline-danger"
                        onClick={() => handleDeleteClick(contact)}
                        className="action-btn delete-btn"
                        title="お問い合わせを削除"
                      >
                        <i className="fas fa-trash"></i>
                        <span className="btn-text">削除</span>
                      </Button>
                    </ButtonGroup>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={5} className="text-center">
                  <div className="no-data">
                    <i className="fas fa-users fa-3x"></i>
                    <h5>お問い合わせデータがありません</h5>
                    <p>
                      お問い合わせ一覧を表示するには、上の「お問い合わせ一覧取得」ボタンをクリックしてください。
                    </p>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>

      {/* 共通ダイアログの呼び出し */}
      <ConfirmDialog
        show={showDialog}
        type={dialogType}
        contact={selectedContact}
        onConfirm={handleDialogConfirm}
        onCancel={() => setShowDialog(false)}
      />
    </>
  );
};

export default ContactTable;
